import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { folderStore } from '../store/store';
import getPrimaryMailboxRootFolder from '../selectors/getPrimaryMailboxRootFolder';
import getFolderTable from '../selectors/getFolderTable';
import getArchiveMailboxRootFolder from '../selectors/getArchiveMailboxRootFolder';
import Folder from '../store/schema/Folder';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FolderForestTreeType } from 'owa-favorites-types';
import getSharedFolderRoot from '../selectors/getSharedFolderRoot';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

export const GEEK_FOLDERS: DistinguishedFolderIdName[] = ['journal', 'outbox', 'syncissues'];

export function getFoldersToShowFirst(): DistinguishedFolderIdName[] {
    const firstFolders: DistinguishedFolderIdName[] = !isConsumer()
        ? ['inbox', 'drafts', 'sentitems', 'scheduled', 'deleteditems', 'junkemail', 'archive']
        : ['inbox', 'junkemail', 'drafts', 'sentitems', 'scheduled', 'deleteditems', 'archive'];

    if (isFeatureEnabled('notes-folder-view')) {
        firstFolders.push('notes');
    }

    return firstFolders;
}

function isOfTypeMailFolder(folder: Folder): boolean {
    return folder && folder.__type == 'Folder:#Exchange';
}

export function shouldDisplayFolderInFolderTree(folder: Folder): boolean {
    // VSO 1192: Support public folder.
    // if (folder.isPublicFolder) {
    //     return true;
    // }

    // Do not show the folder if it's not a mail folder.
    if (!isOfTypeMailFolder(folder)) {
        return false;
    }

    // Suppress hidden and 'geek' folders (outbox, journal, etc).
    if (folder.isHidden || GEEK_FOLDERS.indexOf(folderIdToName(folder.FolderId.Id)) >= 0) {
        return false;
    }

    // Only show the Notes folder if that feature is enabled.
    const userConfiguration = getUserConfiguration();
    if (folderIdToName(folder.FolderId.Id) == 'notes') {
        return (
            userConfiguration.SegmentationSettings.StickyNotes &&
            isFeatureEnabled('cal-stickyNotesFolder')
        );
    }

    // If junk folder, only show if that feature is enabled. If this folder is not any of these cases, then
    // it is a good folder and should be displayed!
    return !(
        folderIdToName(folder.FolderId.Id) == 'junkemail' &&
        !userConfiguration.SegmentationSettings.JunkEMail
    );
}

export function getFolderByDistinguishedId(distinguishedId: DistinguishedFolderIdName): Folder {
    const folderTable = getFolderTable();
    const folderId = folderNameToId(distinguishedId);
    return folderTable.get(folderId);
}

/**
 * get all child folders of a mailbox
 * @param rootFolder - root folder of a mailbox
 * @param principalSMTPAddress - email address of a shared folder
 * @return child folder ids of provided rootfolder id
 */
export function getMailRootFolderChildIds(
    moveToMailboxContext: string,
    principalSMTPAddress?: string
): string[] {
    // If someone wants to get child folders of a shared folder root,
    // then it must pass principalSMTPAddress OR throw error.
    if (moveToMailboxContext === 'sharedFolder' && !principalSMTPAddress) {
        throw new Error('To get all the shared folders you must pass principalSMTPAddress');
    }
    const childFolderIds: string[] = [];
    let rootFolder: Folder;
    switch (moveToMailboxContext) {
        case 'archive':
            rootFolder = getArchiveMailboxRootFolder();
            break;
        case 'sharedFolder':
            rootFolder = getSharedFolderRoot(principalSMTPAddress);
            break;
        default:
            rootFolder = getPrimaryMailboxRootFolder();
    }

    const foldersToShowFirst = getFoldersToShowFirst();

    // First add all the special folders if mailbox is primary and moveto context is primary
    if (
        moveToMailboxContext == 'primary' &&
        rootFolder.DistinguishedFolderId == getPrimaryMailboxRootFolder().DistinguishedFolderId
    ) {
        foldersToShowFirst.forEach(distinguishedFolderId => {
            const folder = getFolderByDistinguishedId(distinguishedFolderId);
            if (
                folder &&
                shouldDisplayFolderInFolderTree(folder) &&
                folder.ParentFolderId.Id === rootFolder.FolderId.Id
            ) {
                childFolderIds.push(folder.FolderId.Id);
            }
        });
    }

    // Get child folder ids for root folder that can be displayed to user
    const childFolderIdsThatCanBeDisplayed = getfolderChildIdsThatCanBeDisplayed(rootFolder);

    // Add all the remaining folders that are not part of the special list above.
    for (let i = 0; i < childFolderIdsThatCanBeDisplayed.length; i++) {
        if (foldersToShowFirst.indexOf(folderIdToName(childFolderIdsThatCanBeDisplayed[i])) == -1) {
            childFolderIds.push(childFolderIdsThatCanBeDisplayed[i]);
        }
    }

    return childFolderIds;
}

/**
 * Identify child folders that can be displayed
 * @param folder - the folder whose displayable children are to be returned
 * @return - all displayable child folders of the parameter folder
 */
export function getfolderChildIdsThatCanBeDisplayed(folder: Folder): string[] {
    const childFolderIds: string[] = [];
    if (folder && folder.childFolderIds) {
        folder.childFolderIds.map(childId => {
            const folder = folderStore.folderTable.get(childId);
            if (folder && shouldDisplayFolderInFolderTree(folder)) {
                childFolderIds.push(folder.FolderId.Id);
            }
        });
    }

    return childFolderIds;
}

/**
 * Function calculates and compares if the treeType of a given folder id is same as given treeType
 * @param folderTreeType - treeType which needs to be checked
 * @param folderId - folderId whose treeType needs to be checked
 */
export function isFolderOfTreeType(
    folderId: string,
    folderTreeType: FolderForestTreeType
): boolean {
    const folderTable = getFolderTable();
    const folder = folderTable.get(folderId);
    return !!(folder && folder.treeType === folderTreeType);
}

/**
 * Function returns folder treeType for the given folderId
 * @param folderId - folderId for which treeType needs to be calculated
 */
export function getTreeTypeFromFolderId(folderId: string): FolderForestTreeType {
    let folderTreeType = null;
    const folderTable = getFolderTable();
    const folder = folderTable.get(folderId);
    if (folder) {
        folderTreeType = folder.treeType;
    }
    return folderTreeType;
}
